import styled, { css } from 'styled-components';
import { useId } from 'react';
import type { InputHTMLAttributes, Ref } from 'react';

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string;
  /** The auth pages' metric set: 40px control, 15px text, 13px label. Default
   *  renders exactly the metrics every other form already has. Not `size` — that
   *  is a native input attribute. */
  large?: boolean;
  // React 19 passes `ref` through to function components as a normal prop (no
  // forwardRef needed) — declared explicitly, exactly like ui/Button's, so
  // callers that need to focus a field imperatively are type-checked.
  // ContactVerifyDialog is the first: it focuses the code field when the
  // confirm step opens, because Dialog only ever focuses its own surface.
  ref?: Ref<HTMLInputElement>;
}

const Wrap = styled.div<{ $large: boolean }>` display: flex; flex-direction: column; gap: ${({ $large }) => ($large ? '4px' : '5px')}; `;
const Label = styled.label<{ $large: boolean }>`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500; color: ${({ theme }) => theme.color.textMuted};
  ${({ $large, theme }) => $large && css`font-size: ${theme.fontSize.sm}; line-height: 18px;`}
`;
const Input = styled.input<{ $invalid: boolean; $large: boolean }>`
  background: ${({ theme, $invalid }) =>
    $invalid
      ? `color-mix(in srgb, ${theme.color.danger} 14%, ${theme.color.surface})`
      : theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md}; padding: 9px 11px;
  color: ${({ theme }) => theme.color.textPrimary}; font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm}; outline: none;
  ${({ $large, theme }) =>
    $large &&
    css`box-sizing: border-box; height: 40px; padding: 0 12px; line-height: 38px; font-size: ${theme.fontSize.md};`}
  &:focus-visible { border-color: ${({ theme }) => theme.color.accentCyan};
    box-shadow: ${({ theme }) => theme.elevation.glow}; }
`;
const Err = styled.span` color: ${({ theme }) => theme.color.danger}; font-size: ${({ theme }) => theme.fontSize.xs}; `;

export function TextField({ label, error, large = false, ...rest }: TextFieldProps) {
  const id = useId();
  const errId = `${id}-err`;
  return (
    <Wrap $large={large}>
      <Label htmlFor={id} $large={large}>{label}</Label>
      <Input id={id} $invalid={!!error} $large={large} aria-invalid={!!error}
        aria-describedby={error ? errId : undefined} {...rest} />
      {error && <Err id={errId}>{error}</Err>}
    </Wrap>
  );
}
